/**
 * L'ÉCHOPPE, LE COFFRE ET LA SOUSCRIPTION — trois volets d'un même écran (design §3).
 *
 * C'est là que le jeu demande de l'argent, et c'est donc là qu'il doit être le moins bavard. Trois
 * règles, tenues d'un bout à l'autre du fichier :
 *
 * - **AUCUN PRIX NE VIENT D'ICI.** Le front n'envoie qu'une référence d'article ou une clef de
 *   produit ; le montant est relu au catalogue par le serveur, à l'ouverture d'une commande comme à
 *   son règlement. Un prix calculé côté client est un prix que le client choisit.
 * - **CE QU'ON NE PEUT PAS PAYER RESTE LISIBLE**, désaturé d'un cran (design §1) : on prépare ses
 *   achats en regardant, et une ligne éteinte est une ligne qu'on ne peut plus peser.
 * - **UN BOUTON QUI NE FAIT RIEN MENT.** Quand la caisse est fermée, les formules s'affichent —
 *   il faut pouvoir savoir ce que ça coûtera — mais rien ne se clique, et une ligne le dit.
 */
import { useState } from "react";

import { useRessource } from "../api/chargement.js";
import { api } from "../api/client.js";
import type { AbonnementEnVue, ArticleDeBoutique, CoffreEnVue, HerosEnVue, MouvementDeFragments, Objet, Vitrine } from "../api/types.js";
import { Glyphe } from "../composants/Glyphe.js";
import { Message } from "../composants/Message.js";
import { Filet, Parchemin } from "../composants/Parchemin.js";
import { messageDe } from "../lexique/erreurs.js";
import {
  CATEGORIES_DE_BOUTIQUE,
  CAUSES_DE_FRAGMENTS,
  CE_QUE_CA_CHANGE,
  catalogueDe,
  dateDite,
  nomDObjet,
  prixDit
} from "../lexique/mots.js";
import { Lien, va } from "../routeur.js";
import { useSession } from "../session.js";
import { t, tn } from "../lexique/langue.js";

/** L'ordre des catégories à l'étal. Il n'est pas alphabétique : on montre d'abord ce qu'on emporte,
 * puis ce qu'on consomme, et le confort en dernier — c'est le seul qui ne se joue pas. */
const ORDRE = ["equipement", "consommable", "option", "confort"] as const;

/**
 * LES TROIS VOLETS. Un seul lien de plus dans l'en-tête (design §3) : la reliure a trois liens et
 * une bascule de thème, en ajouter trois pour vendre aurait fait de la boutique le tiers de la
 * navigation d'un livre-jeu.
 */
export function Volets({ ici }: { ici: "echoppe" | "coffre" | "souscription" }) {
  return (
    <nav className="rang rang--serre volets">
      <Lien vers="/echoppe" className={ici === "echoppe" ? "puce puce--choisie" : "puce"}>
        {t("L'échoppe")}
      </Lien>
      <Lien vers="/coffre" className={ici === "coffre" ? "puce puce--choisie" : "puce"}>
        {t("Le coffre")}
      </Lien>
      <Lien vers="/souscription" className={ici === "souscription" ? "puce puce--choisie" : "puce"}>
        {t("La souscription")}
      </Lien>
    </nav>
  );
}

/**
 * LA BOURSE. Elle se lit en haut de chaque volet, jamais dans l'en-tête : un solde affiché en
 * permanence pendant qu'on lit un nœud, c'est un compteur de jetons d'arcade posé sur une page de
 * roman (design §3).
 */
export function Bourse({ fragments }: { fragments: number }) {
  return (
    <p className="bourse">
      <Glyphe nom="or" taille={18} couleur="var(--or)" />
      <span className="nombre bourse__somme">{fragments}</span>
      <span className="faible">
        {fragments === 0
          ? t("fragments — on en gagne vingt par aventure terminée, cent par boss abattu")
          : t("fragments")}
      </span>
    </p>
  );
}

function Article({
  article,
  fragments,
  surAchat
}: {
  article: ArticleDeBoutique;
  fragments: number;
  surAchat: (ref: string) => Promise<void>;
}) {
  const [enCours, poseEnCours] = useState(false);
  const horsDePrix = fragments < article.fragments;

  return (
    <li className={horsDePrix ? "article article--hors-de-prix" : "article"}>
      <div>
        <span className="article__nom">
          {article.nom}
          {article.quantite > 1 ? <span className="faible"> {t("×")}{article.quantite}</span> : null}
        </span>
        <p className="article__dit">{article.dit}</p>
      </div>
      <div className="article__prix">
        <span className="nombre">{article.fragments}</span>
        <button
          type="button"
          className="bouton bouton--discret"
          disabled={enCours || horsDePrix}
          onClick={() => {
            poseEnCours(true);
            void surAchat(article.ref).finally(() => poseEnCours(false));
          }}
        >
          {horsDePrix ? "Trop cher" : "Prendre"}
        </button>
      </div>
    </li>
  );
}

export function Echoppe() {
  const { compte, rafraichis: rechargeLeCompte } = useSession();
  const etal = useRessource<{ articles: ArticleDeBoutique[]; guichet: Vitrine }>(
    "/boutique",
    (charge: { articles: ArticleDeBoutique[]; guichet: Vitrine }) => charge
  );
  const coffre = useRessource<CoffreEnVue>("/coffre", (charge: CoffreEnVue) => charge);
  const [refus, poseLeRefus] = useState<string | null>(null);
  const [pris, poseLePris] = useState<string | null>(null);

  const fragments = compte?.fragments ?? 0;
  const articles = etal.donnees?.articles ?? [];

  async function achete(ref: string) {
    poseLeRefus(null);
    try {
      await api.poste("/boutique/achats", { ref });
      poseLePris(articles.find((a) => a.ref === ref)?.nom ?? null);
      // TROIS CHOSES BOUGENT D'UN ACHAT : la bourse, le coffre, et les avantages quand c'est du
      // confort. On relit le serveur plutôt que de les recalculer ici — un solde décrémenté à la
      // main est un solde qui finira par différer de celui du grand livre.
      await rechargeLeCompte();
      coffre.recharge();
    } catch (erreur) {
      poseLePris(null);
      poseLeRefus(messageDe(erreur));
    }
  }

  return (
    <div className="page arrivee">
      <header className="entete-decran">
        <h1>{t("L'échoppe")}</h1>
        <Bourse fragments={fragments} />
      </header>

      <Volets ici="echoppe" />

      <p className="douce" style={{ marginTop: "var(--e5)" }}>
        {t("Rien de ce qui est ici ne se trouve QUE ici : tout s'obtient en jouant. Et rien n'entre dans le sac d'un héros directement — ce qu'on achète va au coffre, et l'on prépare son héros avant de partir.")}{" "}
        {coffre.donnees ? (
          <Lien vers="/coffre">
            {tn(coffre.donnees.slots.pris, "Votre coffre : {n} ligne sur {total}.", "Votre coffre : {n} lignes sur {total}.", {
              total: coffre.donnees.slots.total
            })}
          </Lien>
        ) : null}
      </p>

      {pris ? (
        <Message espece="annonce" surFermeture={() => poseLePris(null)}>
          {t("{objet} — au coffre.", { objet: pris })}
        </Message>
      ) : null}
      {refus ? (
        <Message espece="refus" surFermeture={() => poseLeRefus(null)}>
          {refus}
        </Message>
      ) : null}

      {ORDRE.map((categorie) => {
        const dedans = articles.filter((a) => a.categorie === categorie);
        if (!dedans.length) return null;
        const mot = CATEGORIES_DE_BOUTIQUE[categorie]!;
        return (
          <section key={categorie} style={{ marginTop: "var(--e7)" }}>
            <Filet>{mot.nom}</Filet>
            <p className="faible" style={{ marginTop: "var(--e3)" }}>
              {mot.dit}
            </p>
            <ul className="etal">
              {dedans.map((a) => (
                <Article key={a.ref} article={a} fragments={fragments} surAchat={achete} />
              ))}
            </ul>
          </section>
        );
      })}

      <div style={{ marginTop: "var(--e7)" }}>
        <Parchemin variante="creux" titre={t("À court de fragments ?")}>
          <p>
            {t("On en gagne en jouant — vingt pour une aventure menée à son terme, cent pour un boss majeur — et l'on peut en acheter.")} <Lien vers="/souscription">{t("Les paquets sont ici")}</Lien>{t(", avec les formules d'abonnement.")}
          </p>
        </Parchemin>
      </div>
    </div>
  );
}

/* --- Le coffre ------------------------------------------------------------- */

/**
 * LE COFFRE EST UN SAS, ET L'ÉCRAN LE DIT AVEC SES MAINS. Deux colonnes — le coffre, le sac d'un
 * héros —, et un objet passe de l'une à l'autre.
 *
 * QUAND UNE PARTIE EST EN COURS, LES DEUX COLONNES RESTENT AFFICHÉES et les boutons s'éteignent.
 * Cacher le coffre pendant une aventure aurait laissé croire à une panne ; le montrer fermé dit la
 * règle du §8.2 — on prépare son héros avant de partir, on assume ensuite.
 */
export function Coffre() {
  const { compte, rafraichis: rechargeLeCompte } = useSession();
  const coffre = useRessource<CoffreEnVue>("/coffre", (charge: CoffreEnVue) => charge);
  const heros = useRessource<HerosEnVue[]>("/heros", (charge: { heros: HerosEnVue[] }) => charge.heros);
  const parties = useRessource<{ id: string; heros: { id: string } }[]>(
    "/parties",
    (charge: { parties: { id: string; statut: string; heros: { id: string } }[] }) =>
      charge.parties.filter((p) => p.statut === "active" || p.statut === "reprise")
  );
  const [choisi, poseLeChoisi] = useState<string>("");
  const [refus, poseLeRefus] = useState<string | null>(null);
  // Hors partie, on n'a que le socle — c'est ce que le coffre contient, sauf butin d'aventure, qui
  // s'y dira par sa clef plutôt que de faire tomber l'écran (même choix que la fiche).
  const { donnees: objets } = useRessource<Objet[]>("/public/objets", (charge: { objets: Objet[] }) => charge.objets);
  const catalogue = catalogueDe(objets ?? []);

  const vivants = (heros.donnees ?? []).filter((h) => h.status === "alive");
  const herosId = choisi || vivants[0]?.id || "";
  const leHeros = vivants.find((h) => h.id === herosId) ?? null;
  const enRoute = (parties.donnees ?? []).some((p) => p.heros.id === herosId);

  async function bouge(sens: "sorties" | "depots", objetId: string, quantite: number) {
    poseLeRefus(null);
    try {
      await api.poste(`/coffre/${sens}`, { herosId, objetId, quantite });
      coffre.recharge();
      heros.recharge();
      await rechargeLeCompte();
    } catch (erreur) {
      poseLeRefus(messageDe(erreur));
    }
  }

  return (
    <div className="page arrivee">
      <header className="entete-decran">
        <h1>{t("Le coffre")}</h1>
        <Bourse fragments={compte?.fragments ?? 0} />
      </header>

      <Volets ici="coffre" />

      {vivants.length ? (
        <label className="champ" style={{ marginTop: "var(--e5)" }}>
          <span className="capitales">{t("Qui l'on équipe")}</span>
          <select value={herosId} onChange={(e) => poseLeChoisi(e.target.value)}>
            {vivants.map((h) => (
              <option key={h.id} value={h.id}>
                {h.nom}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <p className="douce" style={{ marginTop: "var(--e5)" }}>
          {t("Aucun héros vivant : le coffre attend.")} <Lien vers="/heros/nouveau">{t("En créer un")}</Lien>.
        </p>
      )}

      {enRoute ? (
        <p className="mention mention--verrou" style={{ marginTop: "var(--e4)" }}>
          <Glyphe nom="verrou" taille={14} /> {leHeros?.nom} {t("est en route. On prépare son héros avant de partir, on assume ensuite — le coffre se rouvrira à son retour, ou à sa mort.")}
        </p>
      ) : null}

      {refus ? (
        <Message espece="refus" surFermeture={() => poseLeRefus(null)}>
          {refus}
        </Message>
      ) : null}

      <div className="coffre" style={{ marginTop: "var(--e6)" }}>
        <Parchemin
          variante="creux"
          titre={
            coffre.donnees
              ? t("Au coffre — {pris} / {total} lignes", {
                  pris: coffre.donnees.slots.pris,
                  total: coffre.donnees.slots.total
                })
              : t("Au coffre")
          }
        >
          <p className="faible">{t("Une ligne par sorte d'objet, quelle qu'en soit la quantité.")}</p>
          <ul className="sac">
            {(coffre.donnees?.lignes ?? []).map((l) => (
              <li key={l.objetId}>
                <span>
                  {nomDObjet(l.objetId, catalogue)}
                  <span className="faible"> {t("×")}{l.quantite}</span>
                  {l.option ? <span className="faible"> {t("— se dépense au départ d'une aventure")}</span> : null}
                </span>
                {l.option ? null : (
                  <button
                    type="button"
                    className="bouton bouton--discret"
                    disabled={!leHeros || enRoute}
                    onClick={() => void bouge("sorties", l.objetId, 1)}
                  >
                    {t("Emporter")}
                  </button>
                )}
              </li>
            ))}
          </ul>
          {coffre.donnees && !coffre.donnees.lignes.length ? (
            <p className="douce">{t("Le coffre est vide. Ce qu'on achète à l'échoppe atterrit ici.")}</p>
          ) : null}
        </Parchemin>

        <Parchemin variante="creux" titre={leHeros ? t("Le sac de {heros}", { heros: leHeros.nom }) : t("Le sac")}>
          {leHeros ? (
            <>
              <p className="faible">
                {t("{poids} / {capacite} kg — {pris} / {total} emplacements.", {
                  poids: leHeros.poids.toFixed(1),
                  capacite: leHeros.capacite,
                  pris: leHeros.slots.occupes,
                  total: leHeros.slots.occupes + leHeros.slots.disponibles
                })}
              </p>
              <ul className="sac">
                {leHeros.sac.map((l) => (
                  <li key={l.objetId}>
                    <span>
                      {nomDObjet(l.objetId, catalogue)}
                      <span className="faible"> {t("×")}{l.quantite}</span>
                    </span>
                    <button
                      type="button"
                      className="bouton bouton--discret"
                      disabled={enRoute}
                      onClick={() => void bouge("depots", l.objetId, 1)}
                    >
                      {t("Ranger")}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="douce">{t("Personne à équiper.")}</p>
          )}
        </Parchemin>
      </div>
    </div>
  );
}

/* --- La souscription ------------------------------------------------------- */

export function Souscription() {
  const { compte, rafraichis: rechargeLeCompte } = useSession();
  const etal = useRessource<Vitrine>("/boutique", (charge: { guichet: Vitrine }) => charge.guichet);
  const livre = useRessource<MouvementDeFragments[]>(
    "/fragments",
    (charge: { mouvements: MouvementDeFragments[] }) => charge.mouvements
  );
  const [refus, poseLeRefus] = useState<string | null>(null);
  const [enCours, poseEnCours] = useState<string | null>(null);

  const abonnement = compte?.abonnement ?? null;
  const guichet = etal.donnees;

  /** OUVRIR UNE COMMANDE, PUIS SUIVRE LE GUICHET. Le serveur rend l'URL du prestataire ; on n'en
   * fabrique aucune ici, et l'on n'affiche jamais de montant qui ne viendrait pas de lui. */
  async function commande(produit: string) {
    poseLeRefus(null);
    poseEnCours(produit);
    try {
      const { url } = await api.poste<{ commandeId: string; url: string }>("/commandes", { produit });
      window.location.assign(url);
    } catch (erreur) {
      poseLeRefus(messageDe(erreur));
      poseEnCours(null);
      await rechargeLeCompte();
    }
  }

  return (
    <div className="page arrivee">
      <header className="entete-decran">
        <h1>{t("La souscription")}</h1>
        <Bourse fragments={compte?.fragments ?? 0} />
      </header>

      <Volets ici="souscription" />

      {abonnement ? <Palier abonnement={abonnement} /> : null}

      {refus ? (
        <Message espece="refus" surFermeture={() => poseLeRefus(null)}>
          {refus}
        </Message>
      ) : null}

      {guichet && !guichet.ouvert ? (
        <p className="mention mention--verrou" style={{ marginTop: "var(--e5)" }}>
          <Glyphe nom="verrou" taille={14} /> {t("La caisse n'est pas ouverte pour le moment. Les prix sont ci-dessous, mais rien ne peut être payé — et rien ne sera débité.")}
        </p>
      ) : null}

      <div style={{ marginTop: "var(--e7)" }}>
        <Filet>{t("S'abonner")}</Filet>
      </div>
      <div className="grille grille--cartes" style={{ marginTop: "var(--e5)" }}>
        {["chroniqueur", "archiviste"].map((palier) => {
          const formules = (guichet?.abonnements ?? []).filter((a) => a.palier === palier);
          const actuel = abonnement?.palier === palier;
          return (
            <Parchemin
              key={palier}
              variante={actuel ? "leve" : "pose"}
              titre={palier === "chroniqueur" ? t("Chroniqueur") : t("Archiviste")}
            >
              <ul className="pile pile--serree formule__ajouts">
                {CE_QUE_CA_CHANGE[palier]!.map((ligne) => (
                  <li key={ligne}>{t(ligne)}</li>
                ))}
              </ul>
              <div className="rang rang--serre" style={{ marginTop: "var(--e5)" }}>
                {formules.map((f) => (
                  <button
                    key={f.produit}
                    type="button"
                    className={f.periode === "an" ? "bouton" : "bouton bouton--discret"}
                    disabled={!guichet?.ouvert || enCours !== null}
                    onClick={() => void commande(f.produit)}
                  >
                    {prixDit(f.centimes)} / {f.periode === "an" ? "an" : "mois"}
                  </button>
                ))}
              </div>
              {actuel ? <p className="faible" style={{ marginTop: "var(--e3)" }}>{t("C'est votre palier. Payer à nouveau le prolonge.")}</p> : null}
            </Parchemin>
          );
        })}
      </div>

      <div style={{ marginTop: "var(--e7)" }}>
        <Filet>{t("Des fragments")}</Filet>
      </div>
      <p className="faible" style={{ marginTop: "var(--e3)" }}>
        {t("Ils ne périment pas, et ils ne se remboursent pas en argent. On en gagne aussi en jouant.")}
      </p>
      <ul className="etal">
        {(guichet?.packs ?? []).map((p) => (
          <li key={p.produit} className="article">
            <span className="article__nom">
              <span className="nombre">{p.fragments}</span> {t("fragments")}
            </span>
            <div className="article__prix">
              <span className="nombre">{prixDit(p.centimes)}</span>
              <button
                type="button"
                className="bouton bouton--discret"
                disabled={!guichet?.ouvert || enCours !== null}
                onClick={() => void commande(p.produit)}
              >
                {t("Acheter")}
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div style={{ marginTop: "var(--e7)" }}>
        <Filet>{t("Le grand livre")}</Filet>
      </div>
      <details className="livre-de-compte">
        <summary>{t("D'où viennent vos fragments, et où ils sont allés")}</summary>
        <ul className="sac">
          {(livre.donnees ?? []).map((m) => (
            <li key={`${m.le}-${m.montant}-${m.cause}`}>
              <span>
                {CAUSES_DE_FRAGMENTS[m.cause] ?? m.cause} <span className="faible">— {new Date(m.le).toLocaleDateString("fr-FR")}</span>
              </span>
              <span className="nombre">
                {m.montant > 0 ? "+" : ""}
                {m.montant} <span className="faible">→ {m.solde}</span>
              </span>
            </li>
          ))}
        </ul>
        {livre.donnees && !livre.donnees.length ? <p className="douce">{t("Rien encore.")}</p> : null}
      </details>
    </div>
  );
}

/**
 * LE PALIER DONT ON DISPOSE, ET CE QUI EST INSCRIT AU COMPTE. Quand les deux diffèrent, l'écran
 * l'écrit en toutes lettres et rappelle la règle : rien n'a été retiré (§8.1). C'est la seule façon
 * de ne pas laisser croire qu'on a pris quelque chose.
 */
function Palier({ abonnement }: { abonnement: AbonnementEnVue }) {
  const nom =
    abonnement.palier === "gratuit"
      ? t("Gratuit")
      : abonnement.palier === "chroniqueur"
        ? t("Chroniqueur")
        : t("Archiviste");
  return (
    <Parchemin variante="leve" titre={t("Vous êtes {palier}", { palier: nom })} className="palier">
      {abonnement.echu ? (
        <p>
          {abonnement.expireLe
            ? t("Votre abonnement a expiré le {date}.", { date: dateDite(abonnement.expireLe) })
            : t("Votre abonnement a expiré.")}{" "}
          <strong>{t("Rien ne vous a été retiré")}</strong> {t(": vos héros vivants le restent, votre coffre reste lisible et se vide comme vous voulez. Vous ne pouvez simplement plus en ajouter tant que vous êtes au-dessus du plafond du compte gratuit.")}
        </p>
      ) : abonnement.expireLe ? (
        <p>
          {t(
            "Jusqu'au {date}. Payer à nouveau avant cette date PROLONGE : vous ne perdez pas les jours qui restent.",
            { date: dateDite(abonnement.expireLe) }
          )}
        </p>
      ) : (
        <p className="douce">
          {t("Un héros vivant à la fois, une aventure écrite pour vous par mois, vingt nœuds écoutés par jour. C'est le jeu entier — la mort y est définitive, et c'est ainsi qu'il a été écrit.")}
        </p>
      )}
      <ul className="pile pile--serree faible" style={{ marginTop: "var(--e4)" }}>
        <li>
          {t("Héros vivants :")}{" "}
          <span className="nombre">{abonnement.avantages.herosSimultanes ?? t("sans limite")}</span>
        </li>
        <li>
          {t("Lignes de coffre :")} <span className="nombre">{abonnement.avantages.slotsDeCoffre}</span>
        </li>
        <li>
          {t("Aventures par mois :")}{" "}
          <span className="nombre">
            {abonnement.avantages.aventuresParMois ??
              t("sans plafond ({parJour} / jour)", { parJour: abonnement.avantages.aventuresParJour ?? 0 })}
          </span>
        </li>
        <li>
          {t("Mode Chronique :")}{" "}
          {abonnement.avantages.modeChronique ? t("ouvert") : t("réservé aux abonnés")}
        </li>
      </ul>
    </Parchemin>
  );
}

/* --- La caisse locale ------------------------------------------------------ */

/**
 * LA PAGE DE CAISSE DU GUICHET DE DÉVELOPPEMENT.
 *
 * Elle n'a rien à faire en production, et elle n'y sera jamais atteinte : le serveur n'envoie ici
 * que si `DESTINIA_PAIEMENT=guichet` a été armé à la main, et la route de règlement répond
 * « caisse fermée » dans tous les autres cas.
 *
 * ELLE DIT CE QU'ELLE EST, EN TOUTES LETTRES. Une fausse page de paiement qui ressemblerait à une
 * vraie serait exactement le genre d'écran qu'on oublie de retirer — et le jour où on l'oublie,
 * quelqu'un croira avoir payé.
 *
 * ELLE NE SIGNE RIEN. Le secret du guichet ne quitte jamais le serveur : la page demande, le
 * serveur encaisse. Un secret de webhook envoyé au navigateur serait un secret public.
 */
export function CaisseLocale() {
  const { rafraichis } = useSession();
  const commandeId = new URLSearchParams(window.location.search).get("commande") ?? "";
  const [refus, poseLeRefus] = useState<string | null>(null);
  const [enCours, poseEnCours] = useState(false);

  async function regle() {
    poseLeRefus(null);
    poseEnCours(true);
    try {
      await api.poste(`/paiement/guichet/${commandeId}`);
      await rafraichis();
      va("/souscription");
    } catch (erreur) {
      poseLeRefus(messageDe(erreur));
      poseEnCours(false);
    }
  }

  return (
    <div className="page page--etroite arrivee">
      <header className="entete-decran">
        <h1>{t("Caisse de développement")}</h1>
      </header>
      <Parchemin variante="leve" titre={t("Aucun argent ne circule ici")}>
        <p>
          {t("Ce serveur n'a pas de prestataire de paiement : il tient une caisse locale, pour qu'on puisse voir l'écran d'achat fonctionner sans payer quoi que ce soit. Rien ne sera débité, nulle part, à personne.")}
        </p>
        {refus ? (
          <Message espece="refus" surFermeture={() => poseLeRefus(null)}>
            {refus}
          </Message>
        ) : null}
        <div className="rang" style={{ marginTop: "var(--e5)" }}>
          <button type="button" className="bouton bouton--grand" disabled={enCours || !commandeId} onClick={() => void regle()}>
            {enCours ? t("On encaisse…") : t("Régler pour de faux")}
          </button>
          <Lien vers="/souscription" className="bouton bouton--discret">
            {t("Renoncer")}
          </Lien>
        </div>
      </Parchemin>
    </div>
  );
}
